/**
 * useChecklist Hook
 * Fetches personalized pre-bet checklist from the server.
 */

import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';

export interface ChecklistItem {
  id: string;
  category: 'discipline' | 'bankroll' | 'research' | 'timing';
  text: string;
  reason?: string;
}

interface ChecklistResponse {
  items: ChecklistItem[];
  isPersonalized: boolean;
}

export interface UseChecklistResult {
  items: ChecklistItem[];
  isPersonalized: boolean;
  loading: boolean;
  error: string | null;
}

/** Client-side stale-time: skip fetch if data was loaded within this window */
const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

/** Timestamp of last successful fetch (module-level so it persists across mounts) */
let lastFetchedAt = 0;

export function useChecklist(): UseChecklistResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [isPersonalized, setIsPersonalized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChecklist = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    // Skip fetch if data was recently loaded (stale-time check)
    if (Date.now() - lastFetchedAt < STALE_TIME_MS) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`${API_BASE_URL}/api/analytics/checklist`);
      if (!response.ok) {
        throw new Error('Failed to fetch checklist');
      }

      const result: ChecklistResponse = await response.json();
      setItems(result.items);
      setIsPersonalized(result.isPersonalized);
      lastFetchedAt = Date.now();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch checklist');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  return { items, isPersonalized, loading, error };
}
